import type { SiteSeparation } from '../../schema/siteSeparation/siteSeparation';
import type { DbDiscoveryTask } from '../DiscoveryTasks';
import type { DisabledPostDiscoveryActions } from '../PostDiscoveryActions';

import type { VendorApi } from '../vendorApi';
import type { DashboardTab } from './dashboard';

interface BaseAccess {
  excludeNetworks?: string[];
  expirationDate?: {
    enabled: boolean;
    value?: string | null; // format: "2021-10-07 23:59:59"
  };
  id: string;
  notes?: string;
  password: string | null; // `null` is set when preparing a snapshot for download
  priority: number;
}

export interface CommandLineAccess extends BaseAccess {
  network?: string[];
  privateKey?: string | null; // `null` is set when preparing a snapshot for download
  passphrase?: string | null; // `null` is set when preparing a snapshot for download
  syslog: boolean;
  username: string; // `username` is not required in the request but API stores an empty string in such case
}

export interface EnableModeAccess extends BaseAccess {
  includeNetworks?: string[];
  username: string; // `username` is not required in the request but API stores an empty string in such case
}

export type UsernameBasedAccess = CommandLineAccess | EnableModeAccess;

export const portSettingsProtocols = ['ssh', 'telnet'] as const;
type PortSettingsProtocols = (typeof portSettingsProtocols)[number];

export type Subnet = IPv4Subnet | IPv6Subnet;

/**
 * Subnet in CIDR notation (IPv4)
 * @property broadLong - broadcast address in long format, last IP address in the subnet
 * @property netLong - network address in long format, first IP address in the subnet
 * @property text - CIDR notation
 */
export type IPv4Subnet = {
  broadLong: number;
  netLong: number;
  text: string;
};

/**
 * Subnet in CIDR notation (IPv6)
 * @property max - last IP address in the subnet
 * @property min - first IP address in the subnet
 * @property text - CIDR notation
 *
 * Note: `max` and `min` are in string format because of the large number of digits in IPv6 addresses
 * using BigInt would be more appropriate but it's not supported in JSON.stringify
 */
export type IPv6Subnet = {
  max: string;
  min: string;
  text: string;
};

export interface PortSettingsInternal {
  excludeSubnets: Subnet[];
  label: string | null;
  port: number; // 1 - 65535
  protocol: PortSettingsProtocols;
  subnets: Subnet[];
}
export interface PortSettingsPublic
  extends Omit<PortSettingsInternal, 'excludeSubnets' | 'subnets'> {
  excludeSubnets: string[];
  subnets: string[];
}

export type CliRetryLimits = {
  authFail: number;
  default: number;
  tacacs: {
    delay: number;
    retry: number;
  };
};

export type CliSessionsLimit = {
  enabled: boolean;
  limit: number;
};

export type FullBgpLimit = {
  enabled: boolean;
  threshold: number;
};

export const sourceOfTasks = [
  'aciEndpoint',
  'arp',
  'ndpv6',
  'routes',
  'trace',
  'xdp',
] as const;

export type SourceOfTasks = (typeof sourceOfTasks)[number];

export type LimitDiscoveryTasks = {
  alreadyDiscovered: boolean;
  sourceOfTasks: SourceOfTasks[];
};

export type Networks = {
  exclude: string[];
  include: string[];
  whitelist: string[];
};

export type ResolveNames = {
  discoveryDevices: boolean;
};

export type Scanner = {
  enabled: boolean;
  shortestMask?: number;
};

export type Timeouts = {
  cmdTimeout: number;
  readyTimeout: number;
  unauthenticatedTimeout: number;
  commandResponseTimeoutMinutes: number;
};

export type Trace = {
  port: number;
  protocol: 'icmp' | 'udp' | 'tcp';
  scope: string[];
};

export type VendorApiSettingsInternal = VendorApi & { _key: string };

export type BgpCommunitiesProtocol = 'IPv4' | 'IPv6';

export type BgpCommunities = {
  id: string;
  sn: string; // device serial number
  protocol: BgpCommunitiesProtocol;
  vrf: string | null;
  isVrfStd: boolean;
  communityList: string[];
};

export type ManualLinkLayer = 'L1' | 'L2';

export type ManualLink = {
  id: string;
  srcDeviceSn: string;
  srcInterface: string;
  dstDeviceSn: string;
  dstInterface: string;
  type: ManualLinkLayer;
};

export type AimManualApplication = {
  id: string;

  name: string;
  environment: string | null;

  createdAt: number;
  updatedAt: number;
};

export type AimManualWorkloadInterface = {
  id: string;

  ipAddress: string;
};

export type AimManualWorkload = {
  id: string;
  applicationId: string;

  name: string;
  ipAddress: string | null;

  interfaces: AimManualWorkloadInterface[];

  createdAt: number;
  updatedAt: number;
};

export type AimManualFlow = {
  id: string;
  srcWorkloadId: string;
  dstWorkloadId: string;

  srcIp: string;
  dstIp: string;
  srcPort: number | null;
  dstPort: number | null;
  protocol: number;

  createdAt: number;
  updatedAt: number;
};

export type AimManualSettings = {
  applications: AimManualApplication[];
  workloads: AimManualWorkload[];
  flows: AimManualFlow[];
};

export type AimIntegration = {
  id: string;
  platform: 'Illumio';

  apiUrl: string;
  apiKey: string;
  apiSecret: string;
  organizationId: string;

  slug: string;
  comment: string | null;
};

export type AimSettings = {
  integrations: AimIntegration[];
  manual: AimManualSettings;
};

export type ShowRoute = {
  id: string;
  sn: string;
  networks: string[];
};

/** A Custom Discovery Data definition as the user typed it; discovery validates the YAML. */
export type CustomDiscoveryDefinitionSetting = {
  id: string;
  enabled: boolean;
  yaml: string;
};

/* Settings */
export type InternalDiscoverySettings = {
  allowTelnet: boolean;
  bgp: BgpCommunities[];
  cliRetryLimit: CliRetryLimits;
  cliSessionsLimit: CliSessionsLimit;
  customDiscoveryDefinitions?: CustomDiscoveryDefinitionSetting[]; // Absent in settings of snapshots taken before it existed
  disabledPostDiscoveryActions: DisabledPostDiscoveryActions;
  discoverySeedIpList: string[];
  discoveryHistorySeeds: DiscoveryHistorySeeds;
  discoveryTasks: DbDiscoveryTask[]; // Only tasks that should be excluded from discovery
  enablePwdList: EnableModeAccess[];
  fullBgpIpv6Limit: FullBgpLimit;
  fullBgpLimit: FullBgpLimit;
  limitDiscoveryTasks: LimitDiscoveryTasks;
  networks: Networks;
  ports: PortSettingsInternal[];
  pwdList: CommandLineAccess[];
  resolveNames: ResolveNames;
  scanner: Scanner;
  siteSeparation: SiteSeparation;
  showRoutes: ShowRoute[];
  timeouts: Timeouts;
  trace: Trace;
  vendorApi: VendorApiSettingsInternal[];
  manualLinks: ManualLink[];
  aim: AimSettings;
};

export type AppSyslog = {
  lastPull: number;
  scheduleTime: { expression: string; timezone: string };
  trigger: 'schedule' | 'syslogtrigger';
  triggerTime: number;
};

export type AppBackup = {
  cron: { expression: string; timezone: string };
  directory?: string;
  enabled: boolean;
  password?: string;
  server?: string;
  type: 'local' | 'ftp' | 'sftp';
  username?: string;
};

export type AppMaintenance = {
  cron: { expression: string; timezone: string };
  enabled: boolean;
};

export type AppSnapshots = {
  cron: { expression: string; timezone: string };
  enabled: boolean;
  maxLoaded: number;
  maxLocked: number;
};

export type AppSnapshotsRetention = {
  all: {
    enabled: boolean;
    days: number;
  };
  firstOfDay: {
    enabled: boolean;
    days: number;
  };
  firstOfWeek: {
    enabled: boolean;
    weeks: number;
  };
  firstOfMonth: {
    enabled: boolean;
    months: number;
  };
  firstOfYear: {
    enabled: boolean;
    years: number;
  };
  removalMetrics: {
    hddUtilization: number;
    snapshotsCount: number;
  };
  action: {
    command: 'delete';
    cron: { expression: string; timezone: string };
  };
};

export type AppSnmp = {
  communityString?: string;
  enabled: boolean;
  hostIp?: string[];
  locality?: string;
  systemContact?: string;
  username?: string;
  version?: '2c' | '3';
  encryptionPassphrase?: string;
  passphrase?: string;
  authenticationType?: SnmpAuthenticationType;
};

export type DiscoveryHistorySeeds = {
  enabled: boolean;
  daysLimit: number | null;
  afterDate: 'yyyy-MM-dd' | null;
};

export type InternalApplicationSettings = {
  backup: AppBackup;
  customerName: string;
  dashboard: DashboardTab[];
  maintenance: AppMaintenance;
  snapshots: AppSnapshots;
  snapshotsRetention: AppSnapshotsRetention;
  snmp: AppSnmp;
  syslog: AppSyslog;
};

export type InternalSettings = InternalDiscoverySettings &
  InternalApplicationSettings;

type PublicDiscoverySettingsWithoutTransformation = Pick<
  InternalDiscoverySettings,
  | 'allowTelnet'
  | 'cliRetryLimit'
  | 'cliSessionsLimit'
  | 'customDiscoveryDefinitions'
  | 'disabledPostDiscoveryActions'
  | 'discoveryTasks'
  | 'fullBgpIpv6Limit'
  | 'fullBgpLimit'
  | 'limitDiscoveryTasks'
  | 'networks'
  | 'resolveNames'
  | 'scanner'
> & {
  bgps: InternalDiscoverySettings['bgp'];
};

export type PublicManualLinkType = 'global' | 'snapshot';

export type PublicBaseManualLink = ManualLink & {
  settingType: PublicManualLinkType;
};

export type PublicGlobalManualLink = PublicBaseManualLink & {
  settingType: 'global';
};

export type PublicSnapshotManualLinkStatus = 'valid' | 'invalid';

export type PublicSnapshotManualLinkStatusCode =
  | 'DEVICE_NOT_FOUND'
  | 'SRC_DEVICE_NOT_FOUND'
  | 'DST_DEVICE_NOT_FOUND'
  | 'SRC_INTERFACE_NOT_FOUND'
  | 'DST_INTERFACE_NOT_FOUND'
  | 'LINK_NOT_DISCOVERED';

export type PublicSnapshotManualLink = PublicBaseManualLink & {
  settingType: 'snapshot';
  status: PublicSnapshotManualLinkStatus | null;
  statusCode: PublicSnapshotManualLinkStatusCode | null;
};

export type PublicDiscoverySettings =
  PublicDiscoverySettingsWithoutTransformation & {
    // `custom` flag is used only in the snapshot settings PATCH request to decide if we need to encrypt `password` field
    credentials: (CommandLineAccess & { custom?: boolean })[];
    ports: PortSettingsPublic[];
    privileges: (EnableModeAccess & { custom?: boolean })[];
    seedList: InternalDiscoverySettings['discoverySeedIpList'];
    discoveryHistorySeeds: InternalDiscoverySettings['discoveryHistorySeeds'];
    siteSeparation: SiteSeparation[];
    timeouts: {
      login: InternalDiscoverySettings['timeouts']['readyTimeout'];
      session: InternalDiscoverySettings['timeouts']['cmdTimeout'];
      authentication: InternalDiscoverySettings['timeouts']['unauthenticatedTimeout'];
      command: InternalDiscoverySettings['timeouts']['commandResponseTimeoutMinutes'];
    };
    traceroute: InternalDiscoverySettings['trace'];
    vendorApi: (VendorApi & {
      id: string;
    })[];
    manualLinks: PublicGlobalManualLink[] | PublicSnapshotManualLink[];
  };

type PublicSettingsWithoutTransformation = Pick<
  InternalSettings,
  | 'backup'
  | 'customerName'
  | 'maintenance'
  | 'snapshots'
  | 'snapshotsRetention'
  | 'snmp'
>;

export type PublicSettings = PublicDiscoverySettings &
  PublicSettingsWithoutTransformation & {
    syslog: {
      cron: InternalSettings['syslog']['scheduleTime'];
      time: InternalSettings['syslog']['triggerTime'];
      trigger: InternalSettings['syslog']['trigger'];
    };
  };

export const MIN_SNMP_PASSPHRASE_LENGTH = 8;

export const snmpAuthenticationTypes = [
  'SHA',
  'SHA-224',
  'SHA-256',
  'SHA-384',
  'SHA-512',
] as const;
export type SnmpAuthenticationType = (typeof snmpAuthenticationTypes)[number];

/**
 * Pre-calculated ipv4 column values for table filtering
 * @property value - numeric representation of the IPv4 address
 * @property original - original IPv4 address
 */
export type LoginIpv4 = {
  value: number;
  original: string;
};

/**
 * Pre-calculated ipv6 column values for table filtering
 * @property value - normalized representation of the IPv6 address
 * @property original - original IPv6 address
 */
export type LoginIpv6 = {
  value: string;
  original: string;
};
export const snmpVersions = ['2c', '3'] as const;
export type SnmpVersion = (typeof snmpVersions)[number];
